import {
  searchCategoryWithTransaction,
  searchLocationForTransactionFilter,
  searchPaymentMethod,
  searchProduct,
  searchStaffWithTransaction,
} from "apis";
import {
  FilterBar,
  FilterDateRange,
  FilterMultiSelect,
  FilterTimeRange,
} from "components/commons";
import { useApi, useMount } from "hooks";
import {
  categoryWithTransactionFilterResponse,
  locationWithTransactionFilterResponse,
  paymentMethodList,
  productListResponse,
  staffWithTransactionFilterResponse,
} from "mappers";
import React, { useCallback, useMemo, useState } from "react";
import { mapObjectsToSelect, sortByKeyName } from "services";
import lang from "translations";

const ITEM_FILTER_PAGE_SIZE = 50;
const UNCATEGORIZED_CATEGORY_ID = 0;
const UNLIMITED_DATE_RANGE_DAYS = Number.POSITIVE_INFINITY;

const selectValues = (value) => {
  if (!Array.isArray(value)) return [];
  return value.map((item) => (typeof item === "object" ? item.value : item));
};

const SalesReportFilter = ({
  filterState,
  requestState,
  modifyFilter,
  modifyFilters,
  onClear,
  onApply,
  loading,
}) => {
  const {
    request: searchLocationRequest,
    loading: loadingLocation,
    mappedData: locations,
  } = useApi({
    api: searchLocationForTransactionFilter,
    isArray: true,
    mapper: locationWithTransactionFilterResponse,
  });

  const {
    request: searchCategoryRequest,
    loading: loadingCategory,
    mappedData: categories,
  } = useApi({
    api: searchCategoryWithTransaction,
    isArray: true,
    mapper: categoryWithTransactionFilterResponse,
  });

  const {
    request: searchProductRequest,
  } = useApi({
    api: searchProduct,
    isArray: true,
    mapper: productListResponse,
    returnMappedData: true,
  });
  const [items, setItems] = useState([]);
  const [itemPage, setItemPage] = useState(1);
  const [itemHasMore, setItemHasMore] = useState(false);
  const [loadingItem, setLoadingItem] = useState(false);
  const [loadingMoreItem, setLoadingMoreItem] = useState(false);

  const {
    request: searchPaymentMethodRequest,
    loading: loadingPaymentMethod,
    mappedData: paymentMethods,
  } = useApi({
    api: searchPaymentMethod,
    isArray: true,
    mapper: paymentMethodList,
  });

  const {
    request: searchStaffRequest,
    loading: loadingStaff,
    mappedData: staffs,
  } = useApi({
    api: searchStaffWithTransaction,
    isArray: true,
    mapper: staffWithTransactionFilterResponse,
  });

  const fetchItemOptions = useCallback(
    async ({ venueId, categories = [], page = 1, append = false }) => {
      if (!venueId) return;
      if (page === 1) {
        setLoadingItem(true);
      } else {
        setLoadingMoreItem(true);
      }

      try {
        const mappedPage = await searchProductRequest({
          venueId,
          page,
          pageSize: ITEM_FILTER_PAGE_SIZE,
          categoryIds: selectValues(categories),
          orders: { "p.productName": "asc" },
        });
        const pageItems = (Array.isArray(mappedPage) ? mappedPage : []).filter(
          (item) => item?.id && item?.name,
        );

        setItems((currentItems) => {
          const nextItemsById = new Map((append ? currentItems : []).map((item) => [item.id, item]));
          pageItems.forEach((item) => nextItemsById.set(item.id, item));
          return Array.from(nextItemsById.values());
        });
        setItemPage(page);
        setItemHasMore((mappedPage || []).length === ITEM_FILTER_PAGE_SIZE);
      } catch {
        setItemHasMore(false);
      } finally {
        setLoadingItem(false);
        setLoadingMoreItem(false);
      }
    },
    [searchProductRequest],
  );

  const fetchFilterOptions = useCallback(
    (nextRequestState) => {
      const commonParams = { ...nextRequestState };
      searchLocationRequest({ venueId: commonParams.venueId });
      searchCategoryRequest({ venueId: commonParams.venueId });
      searchStaffRequest({ venueId: commonParams.venueId });
      fetchItemOptions({
        venueId: commonParams.venueId,
        categories: commonParams.categories,
        page: 1,
        append: false,
      });
      searchPaymentMethodRequest({
        venueId: commonParams.venueId,
        page: 1,
        pageSize: 50000,
      });
    },
    [
      searchCategoryRequest,
      fetchItemOptions,
      searchLocationRequest,
      searchPaymentMethodRequest,
      searchStaffRequest,
    ],
  );

  useMount(() => {
    fetchFilterOptions(requestState);
  });

  const changeDateRange = useCallback(
    (value) => {
      modifyFilter("dateRange", { value });
    },
    [modifyFilter],
  );

  const clearFilter = useCallback(() => {
    const { requestState } = onClear();
    fetchFilterOptions(requestState);
  }, [fetchFilterOptions, onClear]);

  const changeCategories = useCallback(
    (value) => {
      const { requestState: nextRequestState } = modifyFilters({ categories: value, items: [] });
      fetchItemOptions({
        venueId: nextRequestState.venueId,
        categories: nextRequestState.categories,
        page: 1,
        append: false,
      });
    },
    [fetchItemOptions, modifyFilters],
  );

  const applyFilter = useCallback(
    async (searchKey) => {
      await onApply(searchKey);
    },
    [onApply],
  );

  const loadMoreItems = useCallback(() => {
    fetchItemOptions({
      venueId: requestState.venueId,
      categories: requestState.categories,
      page: itemPage + 1,
      append: true,
    });
  }, [fetchItemOptions, itemPage, requestState.categories, requestState.venueId]);

  const locationOptions = useMemo(() => {
    return mapObjectsToSelect(locations, { textKey: "name" }).sort(sortByKeyName("text"));
  }, [locations]);

  const categoryOptions = useMemo(() => {
    const options = mapObjectsToSelect(categories, { textKey: "name" }).sort(sortByKeyName("text"));
    const uncategorizedOption = options.find((category) => category.value === UNCATEGORIZED_CATEGORY_ID) || {
      text: lang.uncategorized,
      value: UNCATEGORIZED_CATEGORY_ID,
    };

    return [
      uncategorizedOption,
      ...options.filter((category) => category.value !== UNCATEGORIZED_CATEGORY_ID),
    ];
  }, [categories]);

  const itemOptions = useMemo(() => {
    return mapObjectsToSelect(items, { textKey: "name" }).sort(sortByKeyName("text"));
  }, [items]);

  const paymentMethodOptions = useMemo(() => {
    return mapObjectsToSelect(paymentMethods, { textKey: "name" }).sort(sortByKeyName("text"));
  }, [paymentMethods]);

  const staffOptions = useMemo(() => {
    return mapObjectsToSelect(staffs, { textKey: "name" }).sort(sortByKeyName("text"));
  }, [staffs]);

  return (
    <FilterBar
      className="my-lg"
      filterState={filterState}
      searchBox={false}
      onClear={clearFilter}
      onApply={applyFilter}
      loading={loading}
    >
      <FilterDateRange
        id="dateRange"
        label={lang.dates || lang.dateRange}
        maxDays={UNLIMITED_DATE_RANGE_DAYS}
        value={filterState.dateRange}
        onChange={changeDateRange}
      />
      <FilterTimeRange
        id="timeRange"
        label={lang.time || lang.timeRange}
        value={filterState.dateRange}
        onChange={(value) => modifyFilter("dateRange", { value })}
      />
      <FilterMultiSelect
        id="locations"
        label={lang.outlet}
        allLabel={lang.allLocations}
        emptyLabel={lang.noLocationAvailable}
        disableWhenEmpty={false}
        loading={loadingLocation}
        options={locationOptions}
        value={filterState.locations}
        onChange={(value) => modifyFilter("locations", { value })}
      />
      <FilterMultiSelect
        id="categories"
        label={lang.category}
        allLabel={lang.allCategories}
        emptyLabel={lang.noAvailableCategories}
        disableWhenEmpty={false}
        loading={loadingCategory}
        options={categoryOptions}
        value={filterState.categories}
        onChange={changeCategories}
      />
      <FilterMultiSelect
        id="items"
        label={lang.item}
        allLabel={lang.allItems}
        emptyLabel={lang.noDataAvailable}
        disableWhenEmpty={false}
        loading={loadingItem}
        loadingMore={loadingMoreItem}
        hasMore={itemHasMore}
        options={itemOptions}
        value={filterState.items}
        onLoadMore={loadMoreItems}
        onChange={(value) => modifyFilter("items", { value })}
      />
      <FilterMultiSelect
        id="paymentMethods"
        label={lang.paymentType}
        allLabel={lang.allPaymentTypes}
        emptyLabel={lang.noDataAvailable}
        disableWhenEmpty={false}
        loading={loadingPaymentMethod}
        options={paymentMethodOptions}
        value={filterState.paymentMethods}
        onChange={(value) => modifyFilter("paymentMethods", { value })}
      />
      <FilterMultiSelect
        id="staffs"
        align="right"
        label={lang.staff}
        allLabel={lang.allStaff}
        emptyLabel={lang.noAvailableStaff}
        disableWhenEmpty={false}
        loading={loadingStaff}
        options={staffOptions}
        value={filterState.staffs}
        onChange={(value) => modifyFilter("staffs", { value })}
      />
    </FilterBar>
  );
};

export default SalesReportFilter;
